import React, { useCallback, useMemo } from 'react';
import { getAccHostname } from '@adsk/acs-sc-web-platform-api';
import * as analytics from '@adsk/acs-web-analytics';
import { useAlloyFeatureFlag } from '@adsk/alloy-provider';
import { safeSearchRegex } from '@adsk/alloy-react-helpers';
import { useLocale } from '@adsk/alloy-react-locale';
import { factoryProjectEntitlementsKey } from './factoryProjectEntitlementsKey';
import useSearch from './useSearch';
import { ANALYTICS_TYPES } from '../helpers/analytics';
import { PROJECT_ENTITLEMENTS_CLASSIFICATION, PROJECT_ENTITLEMENTS_FIELDS, PROJECT_ENTITLEMENTS_LIMIT, TABS_NAMES, TEMPLATE_ENTITLEMENTS_CLASSIFICATION, TEMPLATE_ENTITLEMENTS_FIELDS, PROJECT_ENTITLEMENTS_V2_PATH, CLIENTS_PATH } from '../layout/consts';
import ProjectListAccountItem from '../layout/ProjectListAccountItem';
import ProjectListProjectItem from '../layout/ProjectListProjectItem';
import ProjectListTemplateItem from '../layout/ProjectListTemplateItem';
import ProjectRowLoader from '../layout/ProjectRowLoader';
import TemplateRowLoader from '../layout/TemplateRowLoader';
export const useProjectPickerTabs = function (_ref) {
  let {
    search,
    currentTab,
    accounts,
    hasAccounts,
    onClose
  } = _ref;
  const {
    T
  } = useLocale(_ref2 => {
    let {
      locale
    } = _ref2;
    return require("../i18n/".concat(locale, ".json"));
  });
  const {
    value: slashClients,
    loading: slashClientsLoading
  } = useAlloyFeatureFlag('slash-clients');
  const {
    loading: viewAllLinksAtTopFFLoading,
    value: viewAllLinksAtTopFFValue
  } = useAlloyFeatureFlag('project-picker-view-all-projects-link-relocation-to-top');
  const path = slashClients ? CLIENTS_PATH : PROJECT_ENTITLEMENTS_V2_PATH;
  const accountsSearch = useMemo(() => accounts && accounts.filter(a => safeSearchRegex(search).test(a.account_display_name)) || [], [accounts, search]);
  const projectGetKeyMemo = useMemo(() => factoryProjectEntitlementsKey({
    path,
    fields: PROJECT_ENTITLEMENTS_FIELDS,
    classification: PROJECT_ENTITLEMENTS_CLASSIFICATION,
    limit: PROJECT_ENTITLEMENTS_LIMIT
  }), [path]);
  const {
    loading: projectsLoading,
    error: projectsError,
    data: projects,
    totalResults: projectsTotalResults,
    retry: projectsRetry,
    hasNextPage: hasNextProjectsPage,
    nextPage: nextProjectsPage
  } = useSearch({
    enabled: !slashClientsLoading && currentTab === TABS_NAMES.PROJECTS,
    getKey: projectGetKeyMemo,
    search
  });
  const templateGetKeyMemo = useMemo(() => factoryProjectEntitlementsKey({
    path,
    fields: TEMPLATE_ENTITLEMENTS_FIELDS,
    classification: TEMPLATE_ENTITLEMENTS_CLASSIFICATION,
    limit: PROJECT_ENTITLEMENTS_LIMIT
  }), [path]);
  const {
    loading: templatesLoading,
    error: templatesError,
    data: templates,
    totalResults: templatesTotalResults,
    retry: templatesRetry,
    hasNextPage: hasNextTemplatePage,
    nextPage: nextTemplatePage
  } = useSearch({
    enabled: !slashClientsLoading && currentTab === TABS_NAMES.TEMPLATES,
    getKey: templateGetKeyMemo,
    search
  });
  const handleAnalyticOnClick = useCallback(project => {
    if (project) {
      const analyticsType = project.classification === TABS_NAMES.TEMPLATES ? ANALYTICS_TYPES.TOPNAVIGATION_PROJECTPICKER_TEMPLATE_CLICK : ANALYTICS_TYPES.TOPNAVIGATION_PROJECTPICKER_PROJECT_CLICK;
      const data = {
        product_ids: project.products && project.products.map(product => product.key).join(', '),
        product_names: project.products && project.products.map(product => product.name).join(', '),
        project_id: project.id,
        project_name: project.name
      };
      analytics.track(analyticsType, data);
    }
  }, []);
  return useMemo(() => {
    var _T$translate, _T$translate2, _T$translate3;
    return [...(hasAccounts ? [{
      tab: TABS_NAMES.ACCOUNTS,
      label: (_T$translate = T.translate('ACCOUNTS')) === null || _T$translate === void 0 ? void 0 : _T$translate.toString(),
      loading: false,
      error: false,
      hasNextPage: false,
      totalResults: accountsSearch.length,
      data: accountsSearch,
      retry: () => null,
      renderLoader: () => /*#__PURE__*/React.createElement(ProjectRowLoader, null),
      renderItem: _ref3 => {
        let {
          item,
          currentAccountId,
          currentProductId,
          onAccountClick,
          containerRef
        } = _ref3;
        return /*#__PURE__*/React.createElement(ProjectListAccountItem, {
          key: item === null || item === void 0 ? void 0 : item.account_id,
          item: item,
          currentAccountId: currentAccountId,
          currentProductId: currentProductId,
          onClose: onClose,
          onAccountClick: onAccountClick,
          containerRef: containerRef
        });
      }
    }] : []), {
      tab: TABS_NAMES.PROJECTS,
      label: (_T$translate2 = T.translate('PROJECTS')) === null || _T$translate2 === void 0 ? void 0 : _T$translate2.toString(),
      viewAllLabel: T.translate(!viewAllLinksAtTopFFLoading && viewAllLinksAtTopFFValue ? 'PROJECT_PICKER_VIEW_ALL' : 'VIEW_ALL_PROJECTS'),
      viewAllLink: "".concat(getAccHostname(), "/projects"),
      loading: projectsLoading,
      error: projectsError,
      retry: projectsRetry,
      totalResults: projectsTotalResults,
      data: projects,
      fetchNextPage: nextProjectsPage,
      hasNextPage: hasNextProjectsPage,
      renderLoader: () => /*#__PURE__*/React.createElement(ProjectRowLoader, null),
      renderItem: _ref4 => {
        let {
          item,
          currentProjectId,
          currentProductId,
          onProjectClick,
          containerRef
        } = _ref4;
        return /*#__PURE__*/React.createElement(ProjectListProjectItem, {
          key: item === null || item === void 0 ? void 0 : item.id,
          item: item,
          currentProjectId: currentProjectId,
          currentProductId: currentProductId,
          onClose: onClose,
          onProjectClick: onProjectClick,
          handleAnalyticOnClick: handleAnalyticOnClick,
          containerRef: containerRef
        });
      }
    }, {
      tab: TABS_NAMES.TEMPLATES,
      label: (_T$translate3 = T.translate('TEMPLATES')) === null || _T$translate3 === void 0 ? void 0 : _T$translate3.toString(),
      viewAllLabel: T.translate(!viewAllLinksAtTopFFLoading && viewAllLinksAtTopFFValue ? 'PROJECT_PICKER_VIEW_ALL' : 'VIEW_ALL_TEMPLATES'),
      viewAllLink: "".concat(getAccHostname(), "/projects/templates"),
      loading: templatesLoading,
      error: templatesError,
      retry: templatesRetry,
      totalResults: templatesTotalResults,
      data: templates,
      fetchNextPage: nextTemplatePage,
      hasNextPage: hasNextTemplatePage,
      renderLoader: () => /*#__PURE__*/React.createElement(TemplateRowLoader, null),
      renderItem: _ref5 => {
        let {
          item,
          currentProjectId,
          currentProductId,
          onProjectClick,
          containerRef
        } = _ref5;
        return /*#__PURE__*/React.createElement(ProjectListTemplateItem, {
          key: item === null || item === void 0 ? void 0 : item.id,
          item: item,
          currentProjectId: currentProjectId,
          currentProductId: currentProductId,
          onClose: onClose,
          onProjectClick: onProjectClick,
          handleAnalyticOnClick: handleAnalyticOnClick,
          containerRef: containerRef
        });
      }
    }];
  }, [T, accountsSearch, handleAnalyticOnClick, hasAccounts, hasNextProjectsPage, hasNextTemplatePage, nextProjectsPage, nextTemplatePage, onClose, projects, projectsError, projectsLoading, projectsRetry, projectsTotalResults, templates, templatesError, templatesLoading, templatesRetry, templatesTotalResults, viewAllLinksAtTopFFLoading, viewAllLinksAtTopFFValue]);
};
//# sourceMappingURL=useProjectPickerTabs.js.map